/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { format as formatDate } from 'date-fns';
import isNaNLodash from 'lodash/isNaN';
import truncate from 'lodash/truncate';
import { v4 as uuidV4 } from 'uuid';
import {
  ForwardRefExoticComponent,
  RefAttributes,
  useContext,
  useState,
} from 'react';
import { ApolloError, useReactiveVar } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { showNotification, notifications } from '@mantine/notifications';

// UI lib components
import { Box, List, Stack, Text } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';

// Context
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import { PageFileContext } from '@features/Resource/PageFile/context';

// Store
import dialogStoreVar from '@features/Dialog/store/dialogStore';

// Platform
import { isMacOS } from '@helpers/platform';

// Local helpers & utils
import { useSidePanel } from '@layouts/WorkspaceLayout/SidePanelLayout/store/sidePanelState';
import invokePromptConfirm from '@features/Prompt/PromptConfirm';
import invokePromptDelete from '@features/Prompt/PromptDelete';
import { isSpecialUserEmail } from '@shared/constants/users';

// API & Types
import {
  DeleteAction,
  ListResourcesQueryVariables,
  MutationZipFilesAndSendByEmailArgs,
  Permission,
  Resource,
  ResourceType,
  UpdateFavoriteResourcesInput,
  useCreateDownloadActivityMutation,
  useRemoveResourcesMutation,
  useRestoreResourcesMutation,
  useUpdateFavoriteResourcesMutation,
  useZipFilesAndSendByEmailMutation,
  useReprocessResourcesMutation,
} from '@app-types/blackroom-api';
import {
  ActionRole,
  ResourceAction,
  ResourceScope,
} from '@app-types/blackroom-app';
import { ResourceMimeType } from '@app-types/blackroom-app/resourceMimeType';
import { useCopyShareLink } from '@app/features/Resource/hooks/useCopyShareLink';
import {
  getViewAsRequestHeaders,
  getViewAsUserId,
} from '@app/shared/providers/ViewAsProvider/store';

// Assets
import {
  IconShare,
  IconDownload,
  IconExternalLink as IconOpen,
  IconStar as IconFavorite,
  IconStarOff as IconUnfavorite,
  IconCopy,
  IconCursorText as IconRename,
  IconArrowsMove as IconMove,
  IconEyeOff as IconRedact,
  IconTrash as IconArchive,
  IconTrashX as IconDelete,
  IconRestore,
  IconProps,
  IconCut,
  IconRefresh as IconReprocess,
} from '@tabler/icons-react';
import { extractFilenameFromHeaders } from '@features/Resource/utils/extractFilenameFromHeaders';
import useAccount from '@app/features/Account/store/useAccount';
import AIBadge from '../ui-kit/molecules/AIBadge';
import { setClipboardResources } from '@features/Resource/store/copiedResources';

// Constants
const DATE_FORMAT_EXPORT = 'dd-MM-yyyy';
const REQUEST_LIST_SYSTEM_FOLDER_PROTECTED_CODE =
  'REQUEST_LIST_SYSTEM_FOLDER_PROTECTED';

/* -------------------------------------------------------------------------- */
/*                               Implementation                               */
/* -------------------------------------------------------------------------- */

/**
 * Determines whether a file resource is an image
 * @param {Resource} file Resource to check
 * @returns Whether the file is an image
 */
export const isImage = (file: Pick<Resource, 'mimeType'>) =>
  file.mimeType &&
  [
    ResourceMimeType.jpeg,
    ResourceMimeType.png,
    ResourceMimeType.gif,
    ResourceMimeType.bmp,
    ResourceMimeType.svg,
    ResourceMimeType.ico,
  ].includes(file.mimeType as ResourceMimeType);

/**
 * Determines whether a file resource is an office document
 * @param {Resource} file Resource to check
 * @returns Whether the file is an office document
 */
export const isOffice = (file: Pick<Resource, 'mimeType'>) =>
  file.mimeType &&
  [
    ResourceMimeType.doc,
    ResourceMimeType.docx,
    ResourceMimeType.docm,
    ResourceMimeType.dotx,
    ResourceMimeType.dotm,
    ResourceMimeType.ppt,
    ResourceMimeType.pptx,
    ResourceMimeType.pptm,
    ResourceMimeType.potx,
    ResourceMimeType.ppsx,
  ].includes(file.mimeType as ResourceMimeType);

/**
 * Determines whether a file resource is a spreadsheet
 * @param {Resource} file Resource to check
 * @returns Whether the file is a spreadsheet
 */
export const isSpreadsheet = (file: Pick<Resource, 'mimeType'>) =>
  file.mimeType &&
  [
    ResourceMimeType.xls,
    ResourceMimeType.xlsx,
    ResourceMimeType.xlsm,
    // Note: xlsb, xltx, xltm are not supported by the spreadsheet viewer
  ].includes(file.mimeType as ResourceMimeType);

/**
 * Determines whether a file resource is a video
 * @param {Resource} file Resource to check
 * @returns Whether the file is a video
 */
export const isVideo = (file: Pick<Resource, 'mimeType'>) =>
  file.mimeType &&
  [
    ResourceMimeType.mp4,
    ResourceMimeType.mov,
    ResourceMimeType.avi,
    ResourceMimeType.webm,
    ResourceMimeType.mpeg,
    ResourceMimeType.m4v,
    ResourceMimeType.ogg,
  ].includes(file.mimeType as ResourceMimeType);

/**
 * Determines whether a file resource is a Markdown document
 * @param {Resource} file Resource to check
 * @returns Whether the file is Markdown
 */
export const isMarkdown = (file: Pick<Resource, 'mimeType'>) =>
  file.mimeType === ResourceMimeType.md;

/**
 * Determines whether a file resource can be processed as a PDF or image
 * In other words, it can be used to resolve a thumbnail and converted to PDFs
 * @param {Resource} file Resource to check
 * @returns Whether the file processable as PDF
 */
export const isProcessableAsPdf = (file: Pick<Resource, 'type' | 'mimeType'>) =>
  (file.type === ResourceType.File && file.mimeType === ResourceMimeType.pdf) ||
  file.mimeType === ResourceMimeType.md ||
  file.mimeType === ResourceMimeType.plain ||
  isImage(file) ||
  isOffice(file);

/**
 * Identifies changed resources between two resource collections
 * Useful for managing resource table cache refresh rules
 */
export const changedResources = (
  oldResources: Resource[],
  newResources: Resource[],
  returnOld: boolean = false,
) =>
  (returnOld ? oldResources : newResources).filter((or) => {
    // Must be found in old resources as well as have identical update timestamp
    const counterpart = (returnOld ? newResources : oldResources).find(
      (r) => r.id === or.id,
    );
    if (counterpart === undefined || counterpart.updatedAt !== or.updatedAt) {
      return true;
    }
    return counterpart.updatedAt !== or.updatedAt;
  });

export function downloadBlob(blob: Blob, filename: string) {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

/* -------------------------------------------------------------------------- */
/*                         Client-side Access policies                        */
/* -------------------------------------------------------------------------- */

export const canDownload = (document: Pick<Resource, 'type' | 'canDownload'>) =>
  document.canDownload || document.type === ResourceType.Folder;

/**
 * Determines available actions for resource menus and context menus depending on scope
 */
export const getResourceActionsFromScope = (
  scope?: ResourceScope,
): ResourceAction[] => {
  switch (scope) {
    case 'my':
    case 'favorites':
    case 'redacted':
    case 'recent':
      return [
        'open',
        'share',
        'favorite',
        'unfavorite',
        'download',
        'rename',
        'move',
        'archive',
      ];
    case 'archived':
      return ['restore'];

    default:
      return [];
  }
};

/**
 * Extracts the extension of a file from its name.
 */
export const getFileExtension = (filename: string) =>
  filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);

function RestoreDatePicker({
  onDateChange,
}: {
  onDateChange: (date: Date | null) => void;
}) {
  const [date, setDate] = useState<Date | null>(null);
  const { t } = useTranslation();
  return (
    <DateTimePicker
      label={t('Resource:Common.actions.restoreDate')}
      placeholder={t('Resource:Common.actions.restoreDatePlaceholder')}
      value={date}
      onChange={(d) => {
        setDate(d);
        onDateChange(d);
      }}
      clearable
      maxDate={new Date()}
    />
  );
}

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */

export type CreatedByLight = Pick<
  Resource['createdBy'],
  'id' | 'firstName' | 'lastName' | 'email' | 'pictureURL'
>;

export type ActionSignatureArgs = {
  singleResource?: Pick<
    Resource,
    | 'id'
    | 'type'
    | 'name'
    | 'isFavorite'
    | 'pspdfKitId'
    | 'canDownload'
    | 'workspaceId'
    | 'size'
    | 'createdAt'
  > & { createdBy: CreatedByLight };
  resources?: (Pick<
    Resource,
    | 'id'
    | 'type'
    | 'name'
    | 'isFavorite'
    | 'pspdfKitId'
    | 'canDownload'
    | 'workspaceId'
    | 'size'
    | 'createdAt'
  > & { createdBy: CreatedByLight })[];
  singleResourceId?: string;
  resourceIds?: string[];
  resetRowSelection?: () => void;
  options?: {
    selectAll?: boolean;
    isFavorite?: boolean;
    parentId?: string;
    downloadPermission?: Permission;
  };
  requestVariables?: ListResourcesQueryVariables;
};

export type ActionSignature = ({
  singleResource,
  resources,
  singleResourceId,
  resourceIds,
  options,
  requestVariables,
}: ActionSignatureArgs) => void | Promise<void>;

export type ActionDescriptor = {
  iconComponent: ForwardRefExoticComponent<
    IconProps & RefAttributes<SVGSVGElement>
  >;
  action: ActionSignature;
  label: string;
  rank?: number;
  withDivider?: boolean;
  allowedRoles?: ActionRole[];
  badge?: React.ReactNode;
  shortcut?: string;
};

export function useResourceActions() {
  /* ********************************** HOOKS ********************************* */

  // Context
  const workspace = useWorkspace();
  const pageFileContext = useContext(PageFileContext);

  // Auth
  const { getAccessTokenSilently } = useAuth0();
  const { isCurrentWorkspaceAdmin, account } = useAccount();

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);

  // Utilities
  const { copyShareLink } = useCopyShareLink();

  // Routing
  const { scope = 'my' } = useParams<{
    scope: ResourceScope;
    folderId?: string;
  }>() as { scope: ResourceScope; folderId?: string };
  const location = useLocation();

  // API
  const [updateFavoriteResources] = useUpdateFavoriteResourcesMutation();
  const [zipFilesAndSendByEmail] = useZipFilesAndSendByEmailMutation();
  const [createDownloadActivity] = useCreateDownloadActivityMutation();
  const [removeResourcesMutation] = useRemoveResourcesMutation();
  const [restoreResources] = useRestoreResourcesMutation();
  const [reprocessResources] = useReprocessResourcesMutation();

  // UI logic
  const sidePanel = useSidePanel();

  // Localization
  const { t } = useTranslation(['Common', 'Resource', 'Permission', 'Insight']);

  /* ***************************** LOCAL VARIABLES **************************** */

  const isPageFileLocation = /(^|\/)file(\/|$)/.test(location.pathname);

  const workspaceId = workspace.id;

  /* ****************************** LOCAL METHODS ***************************** */

  async function favorite({ resourceIds = [], options }: ActionSignatureArgs) {
    const isFavorite =
      options?.isFavorite !== undefined ? options.isFavorite : true;
    const updateFavoriteResourcesInput: UpdateFavoriteResourcesInput = {
      resources: resourceIds.map((id) => ({
        id,
        isFavorite: isFavorite,
      })),
    };
    await updateFavoriteResources({
      variables: {
        updateFavoriteResourcesInput,
      },
      update: (cache) => {
        const removedIds = resourceIds;

        if (isFavorite || scope !== 'favorites') return;

        cache.modify({
          fields: {
            resources: (existing = { nodes: [] }, { readField }) => {
              const filteredNodes = existing.nodes.filter((ref: any) => {
                const id = readField('id', ref);
                return !removedIds.includes(id as string);
              });
              return {
                ...existing,
                nodes: filteredNodes,
              };
            },
          },
        });
      },
    });
  }

  function deleteResources({
    resources = [],
    options,
    resetRowSelection,
  }: ActionSignatureArgs) {
    const { selectAll, parentId } = options ?? {};
    const deleteAction =
      scope === 'archived' ? DeleteAction.Remove : DeleteAction.Archive;
    const resourceIds = resources.map((r) => r.id);

    if (!selectAll && resourceIds.length === 0) {
      return;
    }

    let entityLabel;
    if (resources.length === 1 && !selectAll) {
      const [firstResource] = resources;
      entityLabel = `${t(`Resource:Common.resource.${firstResource.type}`).toLocaleLowerCase()} ${
        firstResource.name
      }`;
    } else if (selectAll) {
      entityLabel = t(
        'Resource:Common.resource.allResourcesInWorkspace',
      ).toLocaleLowerCase();
    } else {
      entityLabel = `${resources.length} ${t(
        'Resource:Common.resource.resources',
      ).toLocaleLowerCase()}`;
    }

    invokePromptDelete({
      trapFocus: true,
      componentProps: {
        entityLabel,
        deleteAction,
      },
      onConfirm: async () => {
        await removeResourcesMutation({
          variables: {
            workspaceId: workspace.id,
            deleteAction,
            ...(selectAll
              ? {
                  selectAll: true,
                  ...(deleteAction === DeleteAction.Archive && parentId
                    ? { parentId }
                    : {}),
                }
              : { resourceIds }),
          },
          update: (cache) => {
            cache.modify({
              fields: {
                resources: (existing = { nodes: [] }, { readField }) => {
                  if (selectAll) {
                    return { ...existing, nodes: [] };
                  }
                  const filteredNodes = existing.nodes.filter((ref: any) => {
                    const id = readField('id', ref);
                    return !resourceIds.includes(id as string);
                  });
                  return {
                    ...existing,
                    nodes: filteredNodes,
                  };
                },
                resourcesIndexView: (existing = [], { readField }) => {
                  if (selectAll) {
                    return [];
                  }
                  return existing.filter((ref: any) => {
                    const id = readField('id', ref);
                    return !resourceIds.includes(id as string);
                  });
                },
              },
            });
          },
        }).catch((error: ApolloError) => {
          const isSystemFolderProtected = error.graphQLErrors?.some(
            (graphQLError) =>
              graphQLError.extensions?.code ===
              REQUEST_LIST_SYSTEM_FOLDER_PROTECTED_CODE,
          );

          showNotification({
            title: isSystemFolderProtected
              ? t('Resource:Common.errors.systemFolderProtected.title')
              : t('Common:errors.genericTitle'),
            message: isSystemFolderProtected
              ? t('Resource:Common.errors.systemFolderProtected.message')
              : t('Common:errors.genericMessage'),
            color: 'blackroomRed',
          });
          throw error;
        });
        resetRowSelection?.();
      },
    });
  }

  async function executeRestore({
    resources = [],
    selectAll,
    restoreDate,
  }: {
    resources: ActionSignatureArgs['resources'];
    selectAll?: boolean;
    restoreDate?: string;
  }) {
    await restoreResources({
      variables: {
        workspaceId,
        ...(selectAll
          ? { selectAll: true }
          : { resourceIds: resources!.map((r) => r.id) }),
        ...(restoreDate ? { from: restoreDate } : {}),
      },
      update: (cache) => {
        const restoredIds = resources!.map((r) => r.id) ?? [];
        cache.modify({
          fields: {
            resources: (existing = { nodes: [] }, { readField }) => {
              if (selectAll) {
                return { nodes: [] };
              }

              const filteredNodes = existing.nodes.filter((ref: any) => {
                const id = readField('id', ref);
                return !restoredIds.includes(id as string);
              });
              return {
                ...existing,
                nodes: filteredNodes,
              };
            },
          },
        });
      },
    });
  }

  async function restoreResourcesAction({
    resources = [],
    options,
  }: ActionSignatureArgs) {
    const { selectAll } = options ?? {};
    const isInternalUser = isSpecialUserEmail(account?.email);

    if (isInternalUser) {
      let selectedDate: Date | null = null;

      invokePromptConfirm({
        componentProps: {
          modalContent: (
            <Stack>
              <Text size="lg" fw={600} ta="center">
                {t('Resource:Common.actions.restore')}
              </Text>
              <RestoreDatePicker
                onDateChange={(date) => {
                  selectedDate = date;
                }}
              />
            </Stack>
          ),
        },
        onConfirm: () =>
          executeRestore({
            resources,
            selectAll,
            restoreDate: selectedDate?.toISOString(),
          }),
      });
      return;
    }

    await executeRestore({ resources, selectAll });
  }

  /* ********************************* ACTIONS ******************************** */

  // Maps toolbar buttons / menu context items to icon and allowed roles

  return {
    open: {
      label: t('Resource:Common.actions.open'),
      iconComponent: IconOpen,
      action: function open({ resources = [] }) {
        const [firstResource] = resources;
        const targetWindow = window.open(
          `/workspace/${firstResource.workspaceId}/${firstResource.type}/${firstResource.id}`,
          '_blank',
        );
        targetWindow?.focus();
      },
      rank: 0,
    },
    share: {
      label: t('Resource:Common.actions.share'),
      iconComponent: IconShare,
      action: function share({ resources = [] }) {
        const [firstResource] = resources;
        copyShareLink(firstResource);
      },
      rank: 1,
    },
    favorite: {
      label: t('Resource:Common.actions.favorite'),
      iconComponent: IconFavorite,
      action: favorite,
      rank: 2,
    },
    unfavorite: {
      label: t('Resource:Common.actions.unfavorite'),
      iconComponent: IconUnfavorite,
      action: async function unfavorite({ resources = [] }) {
        await favorite({
          resourceIds: resources.map((r) => r.id),
          options: { isFavorite: false },
        });
      },
      rank: 3,
    },
    download: {
      label: t('Resource:Common.actions.download'),
      iconComponent: IconDownload,
      action: async function download({ resources = [], options }) {
        const downloadPermission = options?.downloadPermission;
        const downloadPermissionQueryString = downloadPermission
          ? `?${new URLSearchParams({ permission: downloadPermission }).toString()}`
          : '';

        if (options?.selectAll) {
          const operationId = `operation.download.zipEmailDownload.${uuidV4()}`;
          showNotification({
            id: operationId,
            title: t(
              isCurrentWorkspaceAdmin
                ? 'Resource:Common.operation.download.zipEmailDownload.title'
                : 'Resource:Common.operation.download.zipEmailDownload.memberTitle',
            ),
            message: t(
              isCurrentWorkspaceAdmin
                ? 'Resource:Common.operation.download.zipEmailDownload.message'
                : 'Resource:Common.operation.download.zipEmailDownload.memberMessage',
            ),
            color: 'blackroomGreen',
          });

          await zipFilesAndSendByEmail({
            variables: {
              resourceIds: [],
              workspaceId,
              selectAll: true,
              parentId: options.parentId,
              permission: downloadPermission,
            },
          }).catch((error: ApolloError) => {
            let hasForbiddenError = false;
            if (error.graphQLErrors) {
              hasForbiddenError = error.graphQLErrors.some(
                (graphQLError) => graphQLError.extensions?.code === 'FORBIDDEN',
              );
            }

            notifications.hide(operationId);
            if (hasForbiddenError) {
              showNotification({
                title: t(
                  'Resource:Common.operation.download.streamDownload.permissionErrorTitle',
                ),
                message: t(
                  'Resource:Common.operation.download.streamDownload.permissionErrorMessage',
                ),
                color: 'blackroomRed',
              });
            } else {
              showNotification({
                title: t('Common:errors.genericTitle'),
                message: t('Common:errors.genericMessage'),
                color: 'blackroomRed',
              });
            }
          });

          return;
        }

        const downloadSizeThreshold = isNaNLodash(
          Number(import.meta.env.VITE_APP_RESOURCES_DOWNLOAD_SIZE_THRESHOLD_MB),
        )
          ? 500
          : Number(
              import.meta.env.VITE_APP_RESOURCES_DOWNLOAD_SIZE_THRESHOLD_MB,
            );
        if (resources.length === 1) {
          const [singleResource] = resources;
          if (singleResource.size / 1024 / 1024 <= downloadSizeThreshold) {
            if (singleResource.type === ResourceType.Folder) {
              const token = await getAccessTokenSilently();
              const operationId = `operation.download.streamDownload.${uuidV4()}`;

              if (isCurrentWorkspaceAdmin) {
                showNotification({
                  id: operationId,
                  title: t(
                    'Resource:Common.operation.download.streamDownload.title',
                    {
                      numberOfResources: 1,
                    },
                  ),
                  message: t(
                    'Resource:Common.operation.download.streamDownload.message',
                    {
                      truncatedResourceName: truncate(singleResource.name),
                    },
                  ),
                  color: 'blackroomBlue',
                  autoClose: false,
                });
              } else {
                showNotification({
                  id: operationId,
                  title: t(
                    'Resource:Common.operation.download.streamDownload.memberTitle',
                  ),
                  message: t(
                    'Resource:Common.operation.download.streamDownload.message',
                    {
                      truncatedResourceName: truncate(singleResource.name),
                    },
                  ),
                  color: 'blackroomBlue',
                });
              }

              const res = await fetch(
                `${import.meta.env.VITE_API_CORE_REST_URL}/resources/zip/${workspaceId}/${singleResource.id}${downloadPermissionQueryString}`,
                {
                  headers: {
                    authorization: token ? `Bearer ${token}` : '',
                    ...getViewAsRequestHeaders(),
                  },
                },
              );

              if (res.status === 202) {
                notifications.hide(operationId);
                showNotification({
                  title: t(
                    isCurrentWorkspaceAdmin
                      ? 'Resource:Common.operation.download.zipEmailDownload.title'
                      : 'Resource:Common.operation.download.zipEmailDownload.memberTitle',
                  ),
                  message: t(
                    isCurrentWorkspaceAdmin
                      ? 'Resource:Common.operation.download.zipEmailDownload.message'
                      : 'Resource:Common.operation.download.zipEmailDownload.memberMessage',
                  ),
                  color: 'blackroomGreen',
                });
                return;
              }

              if (res.status === 403) {
                notifications.hide(operationId);
                showNotification({
                  title: t(
                    'Resource:Common.operation.download.streamDownload.permissionErrorTitle',
                  ),
                  message: t(
                    'Resource:Common.operation.download.streamDownload.permissionErrorMessage',
                  ),
                  color: 'blackroomRed',
                });
                return;
              }

              if (res.status !== 200) {
                return;
              }

              const blob = await res.blob();
              notifications.hide(operationId);
              downloadBlob(blob, `${singleResource.name}.zip`);
              return;
            }

            // Regular / single file
            const token = await getAccessTokenSilently();
            const res = await fetch(
              `${import.meta.env.VITE_API_CORE_REST_URL}/resources/download/${workspaceId}/${singleResource.id}${downloadPermissionQueryString}`,
              {
                headers: {
                  authorization: token ? `Bearer ${token}` : '',
                  ...getViewAsRequestHeaders(),
                },
              },
            );

            if (res.status === 202) {
              showNotification({
                title: t(
                  isCurrentWorkspaceAdmin
                    ? 'Resource:Common.operation.download.zipEmailDownload.title'
                    : 'Resource:Common.operation.download.zipEmailDownload.memberTitle',
                ),
                message: t(
                  isCurrentWorkspaceAdmin
                    ? 'Resource:Common.operation.download.zipEmailDownload.message'
                    : 'Resource:Common.operation.download.zipEmailDownload.memberMessage',
                ),
                color: 'blackroomGreen',
              });
              return;
            }

            if (res.status !== 200) {
              return;
            }

            const blob = await res.blob();
            downloadBlob(
              blob,
              extractFilenameFromHeaders(res.headers, singleResource.name),
            );
            if (!getViewAsUserId()) {
              await createDownloadActivity({
                variables: { resourceId: singleResource.id },
              });
            }
          } else {
            const zipFilesAndSendByEmailVariables: MutationZipFilesAndSendByEmailArgs =
              {
                resourceIds: [singleResource.id],
                workspaceId,
                permission: downloadPermission,
              };
            await zipFilesAndSendByEmail({
              variables: zipFilesAndSendByEmailVariables,
            })
              .then(() => {
                showNotification({
                  title: t(
                    isCurrentWorkspaceAdmin
                      ? 'Resource:Common.operation.download.zipEmailDownload.title'
                      : 'Resource:Common.operation.download.zipEmailDownload.memberTitle',
                  ),
                  message: t(
                    isCurrentWorkspaceAdmin
                      ? 'Resource:Common.operation.download.zipEmailDownload.message'
                      : 'Resource:Common.operation.download.zipEmailDownload.memberMessage',
                  ),
                  color: 'blackroomGreen',
                });
              })
              .catch((error: ApolloError) => {
                let hasForbiddenError = false;
                if (error.graphQLErrors) {
                  hasForbiddenError = error.graphQLErrors.some(
                    (graphQLError) =>
                      graphQLError.extensions?.code === 'FORBIDDEN',
                  );
                }

                if (hasForbiddenError) {
                  showNotification({
                    title: t(
                      'Resource:Common.operation.download.streamDownload.permissionErrorTitle',
                    ),
                    message: t(
                      'Resource:Common.operation.download.streamDownload.permissionErrorMessage',
                    ),
                    color: 'blackroomRed',
                  });
                } else {
                  showNotification({
                    title: t('Common:errors.genericTitle'),
                    message: t('Common:errors.genericMessage'),
                    color: 'blackroomRed',
                  });
                }
              });
          }
        } else {
          const downloadableResources = resources.filter((r) => canDownload(r));

          const fileSizes = downloadableResources.reduce(
            (totalSize, r) => (r.size ?? 0) / 1024 / 1024 + totalSize,
            0,
          );

          /*
            if files size is greater than 5MB, we zip them in a lambda and send them by email
          */
          if (fileSizes > downloadSizeThreshold) {
            const zipFilesAndSendByEmailVariables: MutationZipFilesAndSendByEmailArgs =
              {
                resourceIds: downloadableResources.map((r) => r.id),
                workspaceId,
                permission: downloadPermission,
              };
            await zipFilesAndSendByEmail({
              variables: zipFilesAndSendByEmailVariables,
            })
              .then(() => {
                showNotification({
                  title: t(
                    isCurrentWorkspaceAdmin
                      ? 'Resource:Common.operation.download.zipEmailDownload.title'
                      : 'Resource:Common.operation.download.zipEmailDownload.memberTitle',
                  ),
                  message: t(
                    isCurrentWorkspaceAdmin
                      ? 'Resource:Common.operation.download.zipEmailDownload.message'
                      : 'Resource:Common.operation.download.zipEmailDownload.memberMessage',
                  ),
                  color: 'blackroomGreen',
                });
              })
              .catch((error: ApolloError) => {
                let hasForbiddenError = false;
                if (error.graphQLErrors) {
                  hasForbiddenError = error.graphQLErrors.some(
                    (graphQLError) =>
                      graphQLError.extensions?.code === 'FORBIDDEN',
                  );
                }

                if (hasForbiddenError) {
                  showNotification({
                    title: t(
                      'Resource:Common.operation.download.streamDownload.permissionErrorTitle',
                    ),
                    message: t(
                      'Resource:Common.operation.download.streamDownload.permissionErrorMessage',
                    ),
                    color: 'blackroomRed',
                  });
                } else {
                  showNotification({
                    title: t('Common:errors.genericTitle'),
                    message: t('Common:errors.genericMessage'),
                    color: 'blackroomRed',
                  });
                }
              });
            return;
          }

          const performDownload = async () => {
            const archiveName = `blackroom-${workspace.name}-${formatDate(
              new Date(),
              DATE_FORMAT_EXPORT,
            )}`;

            const token = await getAccessTokenSilently();
            const operationId = `operation.download.streamDownload.${uuidV4()}`;
            if (isCurrentWorkspaceAdmin) {
              showNotification({
                id: operationId,
                title: t(
                  'Resource:Common.operation.download.streamDownload.title',
                  {
                    numberOfResources: resources.length,
                  },
                ),
                message: (
                  <Stack gap={5}>
                    {resources.slice(0, 3).map((r) => (
                      <Box key={r.id}>
                        {t(
                          'Resource:Common.operation.download.streamDownload.message',
                          {
                            truncatedResourceName: truncate(r.name),
                          },
                        )}
                      </Box>
                    ))}
                    {resources.length > 3 && (
                      <Box>
                        {t(
                          'Resource:Common.operation.download.streamDownload.moreResources',
                          {
                            count: resources.length - 3,
                          },
                        )}
                      </Box>
                    )}
                  </Stack>
                ),
                color: 'blackroomBlue',
                autoClose: false,
              });
            } else {
              showNotification({
                id: operationId,
                title: t(
                  'Resource:Common.operation.download.streamDownload.memberTitle',
                ),
                message: (
                  <Stack gap={5}>
                    {resources.slice(0, 3).map((r) => (
                      <Box key={r.id}>
                        {t(
                          'Resource:Common.operation.download.streamDownload.message',
                          {
                            truncatedResourceName: truncate(r.name),
                          },
                        )}
                      </Box>
                    ))}
                    {resources.length > 3 && (
                      <Box>
                        {t(
                          'Resource:Common.operation.download.streamDownload.moreResources',
                          {
                            count: resources.length - 3,
                          },
                        )}
                      </Box>
                    )}
                  </Stack>
                ),
                color: 'blackroomBlue',
              });
            }

            const res = await fetch(
              `${import.meta.env.VITE_API_CORE_REST_URL}/resources/zip/${workspaceId}/${downloadableResources
                .map((r) => r.id)
                .join(',')}${downloadPermissionQueryString}`,
              {
                headers: {
                  authorization: token ? `Bearer ${token}` : '',
                  ...getViewAsRequestHeaders(),
                },
              },
            );

            if (res.status === 202) {
              notifications.hide(operationId);
              showNotification({
                title: t(
                  isCurrentWorkspaceAdmin
                    ? 'Resource:Common.operation.download.zipEmailDownload.title'
                    : 'Resource:Common.operation.download.zipEmailDownload.memberTitle',
                ),
                message: t(
                  isCurrentWorkspaceAdmin
                    ? 'Resource:Common.operation.download.zipEmailDownload.message'
                    : 'Resource:Common.operation.download.zipEmailDownload.memberMessage',
                ),
                color: 'blackroomGreen',
              });
              return;
            }

            if (res.status === 403) {
              notifications.hide(operationId);
              showNotification({
                title: t(
                  'Resource:Common.operation.download.streamDownload.permissionErrorTitle',
                ),
                message: t(
                  'Resource:Common.operation.download.streamDownload.permissionErrorMessage',
                ),
                color: 'blackroomRed',
              });
              return;
            }

            if (res.status !== 200) {
              return;
            }

            const blob = await res.blob();
            notifications.hide(operationId);
            downloadBlob(blob, `${archiveName}.zip`);
          };

          const discardedResources = resources.filter(
            (r) => r.type === ResourceType.File && canDownload(r) === false,
          );
          if (discardedResources.length > 0) {
            invokePromptConfirm({
              componentProps: {
                modalContent: (
                  <Stack>
                    <Trans
                      i18nKey="Resource:Common.operation.download.promptBulkDownloadPermissionRestriction.messageOpening"
                      transSupportBasicHtmlNodes
                    />
                    <List>
                      {discardedResources.map((r, i: number) => (
                        <List.Item key={r.id}>
                          <Text>{r.name}</Text>
                        </List.Item>
                      ))}
                    </List>
                    <Trans
                      i18nKey="Resource:Common.operation.download.promptBulkDownloadPermissionRestriction.messageClosing"
                      transSupportBasicHtmlNodes
                    />
                  </Stack>
                ),
                entityLabel:
                  t(
                    'Resource:Common.operation.download.promptBulkDownloadPermissionRestriction.message',
                    {
                      fileNames: discardedResources.map((r) => r.name),
                    },
                  ) ?? '',
              },
              confirmProps: { color: 'blackroomOrange' },
              onConfirm: async () => {
                await performDownload();
              },
            });
          } else {
            await performDownload();
          }
        }
      },
      rank: 4,
    },
    copy: {
      label: t('Resource:Common.actions.copy'),
      iconComponent: IconCopy,
      action: function copy({ resources = [] }) {
        const resourceIds = resources.map((r) => r.id);
        setClipboardResources(resourceIds, 'copy');

        showNotification({
          title: t('Resource:Common.notifications.resourcesCopied.title', {
            count: resources.length,
          }),
          message: t('Resource:Common.notifications.resourcesCopied.message'),
          color: 'blackroomBlue',
        });
      },
      allowedRoles: ['workspace-admin', 'workspace-editor'],
      rank: 5,
      shortcut: isMacOS ? '⌘C' : 'Ctrl+C',
    },
    cut: {
      label: t('Resource:Common.actions.cut'),
      iconComponent: IconCut,
      action: function cut({ resources = [] }) {
        const resourceIds = resources.map((r) => r.id);
        setClipboardResources(resourceIds, 'cut');

        showNotification({
          title: t('Resource:Common.notifications.resourcesCut.title', {
            count: resourceIds.length,
          }),
          message: t('Resource:Common.notifications.resourcesCut.message'),
          color: 'blackroomBlue',
        });
      },
      allowedRoles: ['workspace-admin', 'workspace-editor'],
      rank: 6,
      shortcut: isMacOS ? '⌘X' : 'Ctrl+X',
    },
    move: {
      label: t('Resource:Common.actions.move'),
      iconComponent: IconMove,
      action: function move({
        resources = [],
        resetRowSelection,
        requestVariables,
      }) {
        const [firstResource] = resources;
        dialogStore?.invokeDialog?.('dialog-resource-move', {
          modalProps: {
            title: t('Resource:DialogResourceMove.title', {
              resourceType: t(
                `Resource:Common.resource.${resources.length === 1 ? firstResource.type : 'resources'}`,
              ),
            }),
          },
          dialogResources: resources,
          resetRowSelection,
          requestVariables,
        });
      },
      allowedRoles: ['workspace-admin', 'workspace-editor'],
      rank: 7,
    },
    rename: {
      label: t('Resource:Common.actions.rename'),
      iconComponent: IconRename,
      badge: <AIBadge size="sm" />,
      action: async function rename({ resources = [] }) {
        if (resources.length === 1) {
          const [singleResource] = resources;
          dialogStore?.invokeDialog?.('dialog-resource-edit', {
            modalProps: {
              title: t('Resource:DialogResourceEdit.titleEdit', {
                resourceType: t(
                  `Resource:Common.resource.${singleResource.type}`,
                ),
              }),
            },
            dialogResources: [singleResource],
          });
        } else {
          dialogStore?.invokeDialog?.('dialog-resource-edit', {
            modalProps: {
              title: (
                <Stack gap={2}>
                  <Text fw={600} size="lg">
                    {t('Resource:DialogResourceEdit.titleBulk')}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {t('Resource:DialogResourceEdit.selectedCount', {
                      count: resources.length,
                    })}
                  </Text>
                </Stack>
              ),
            },
            dialogResources: resources,
          });
        }
      },
      allowedRoles: ['workspace-admin', 'workspace-editor'],
      rank: 8,
    },
    redact: {
      label: t('Resource:Common.actions.redact'),
      iconComponent: IconRedact,
      action: function redact({ resources = [] }) {
        if (isPageFileLocation && pageFileContext.selectedText) {
          sidePanel.toggle();
        } else {
          dialogStore?.invokeDialog?.('dialog-redaction', {
            resourcesToRedact: resources,
          });
        }
      },
      allowedRoles: ['workspace-admin'],
      rank: 9,
    },
    reprocess: {
      label: t('Resource:Common.actions.reprocess'),
      iconComponent: IconReprocess,
      action: async function reprocess({
        resourceIds = [],
        resetRowSelection,
      }) {
        try {
          await reprocessResources({
            variables: { resourceIds },
          });
          showNotification({
            title: t('Resource:Common.actions.reprocess'),
            message: t('Resource:ReprocessResources.success'),
            color: 'blackroomGreen',
          });
        } catch {
          showNotification({
            title: t('Common:errors.genericTitle'),
            message: t('Resource:ReprocessResources.error'),
            color: 'blackroomRed',
          });
        } finally {
          resetRowSelection?.();
        }
      },
      allowedRoles: ['workspace-admin', 'workspace-editor'],
      rank: 10.5,
    },
    archive: {
      label: t('Resource:Common.actions.archive'),
      iconComponent: IconArchive,
      action: deleteResources,
      allowedRoles: ['workspace-admin', 'workspace-editor'],
      rank: 11,
      shortcut: isMacOS ? '⌘ Del' : 'Ctrl+Del',
    },
    restore: {
      label: t('Resource:Common.actions.restore'),
      iconComponent: IconRestore,
      action: restoreResourcesAction,
      allowedRoles: ['workspace-admin', 'workspace-editor'],
      rank: 12,
    },
    delete: {
      label: t('Resource:Common.actions.delete'),
      iconComponent: IconDelete,
      action: deleteResources,
      allowedRoles: ['workspace-admin', 'workspace-editor'],
      rank: 13,
      shortcut: isMacOS ? '⌘ Del' : 'Ctrl+Del',
    },
  } satisfies Record<ResourceAction, ActionDescriptor>;
}
